import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Package, Truck, Store, DollarSign, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const iconByTipo = {
  nueva_asignacion: Truck,
  repartidor_en_tienda: Store,
  aprobacion: ShieldCheck,
  nuevo_pedido: Package,
  venta_despachada: Truck,
  venta_entregada: DollarSign,
  comision_abonada: DollarSign,
};

export default function NotificationBell() {
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const seenRef = useRef(new Set()); // ids ya toasted
  const firstLoadRef = useRef(true);

  const load = async () => {
    try {
      const { data } = await api.get("/notificaciones/mine");
      const nuevas = data.notificaciones.filter(n => !seenRef.current.has(n.id));
      if (!firstLoadRef.current) {
        nuevas.filter(n => !n.leida).forEach(n => {
          toast.info(n.mensaje, { duration: 6000, description: new Date(n.created_at).toLocaleTimeString() });
        });
      }
      firstLoadRef.current = false;
      data.notificaciones.forEach(n => seenRef.current.add(n.id));
      setNotifs(data.notificaciones);
      setUnread(data.unread);
    } catch (e) {
      // Silencioso si es guest (401) — logea otros errores
      if (e?.response?.status !== 401) console.error("NotificationBell load error:", e);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, []);

  const marcarLeida = async (id) => {
    try {
      await api.patch(`/notificaciones/${id}/leer`);
      setNotifs(ns => ns.map(n => n.id === id ? { ...n, leida: true } : n));
      setUnread(u => Math.max(0, u - 1));
    } catch (e) { console.error("marcarLeida error:", e); }
  };

  const marcarTodas = async () => {
    try {
      await api.post("/notificaciones/marcar-todas-leidas");
      setNotifs(ns => ns.map(n => ({ ...n, leida: true })));
      setUnread(0);
    } catch (e) { console.error("marcarTodas error:", e); }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell" className="relative p-2 hover:bg-zinc-100 rounded-sm transition-colors">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-1 -right-1 bg-[#FF3366] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 grid place-items-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-[500px] overflow-y-auto p-0">
        <div className="flex items-center justify-between p-3 border-b border-zinc-200">
          <div>
            <div className="font-display font-bold text-sm">Notificaciones</div>
            <div className="text-xs text-zinc-500">{unread} sin leer</div>
          </div>
          {unread > 0 && (
            <button data-testid="notif-mark-all" onClick={marcarTodas} className="text-xs text-[#FF3366] font-medium hover:underline flex items-center gap-1">
              <CheckCheck className="w-3.5 h-3.5" /> Marcar todas
            </button>
          )}
        </div>
        <div className="divide-y divide-zinc-100">
          {notifs.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-400">Sin notificaciones aún</div>
          ) : (
            notifs.map(n => {
              const Icon = iconByTipo[n.tipo] || Bell;
              return (
                <button
                  key={n.id}
                  data-testid={`notif-${n.id}`}
                  onClick={() => !n.leida && marcarLeida(n.id)}
                  className={`w-full text-left p-3 flex gap-3 items-start hover:bg-zinc-50 transition-colors ${!n.leida ? "bg-[#FF3366]/5" : ""}`}
                >
                  <div className={`shrink-0 w-8 h-8 rounded-sm grid place-items-center ${!n.leida ? "bg-[#FF3366] text-white" : "bg-zinc-100 text-zinc-500"}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm leading-snug ${!n.leida ? "font-medium text-zinc-900" : "text-zinc-600"}`}>
                      {n.mensaje}
                    </div>
                    <div className="text-xs text-zinc-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  {!n.leida && <span className="w-2 h-2 rounded-full bg-[#FF3366] shrink-0 mt-2" />}
                </button>
              );
            })
          )}
        </div>
        {notifs.length > 0 && (
          <div className="p-2 border-t border-zinc-200 text-center">
            <Link to="/dashboard" className="text-xs text-zinc-500 hover:text-[#FF3366]">Ir a mi panel</Link>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
